import { useEffect } from "react";

// Imperatively set the page title and head meta tags.
//
// Replaces <Helmet> usage across the app. We hit a known race in
// react-helmet-async (and its forks) under React 18 StrictMode + Vite's
// lazy-loaded routes where the title would not update on client-side
// navigation — the parent Helmet "won" the title stack during the
// Suspense fallback / mount-unmount-remount cycle. useEffect runs after
// commit so it cleanly applies the latest title for whichever page is
// currently mounted, with no library coordination needed.
//
// Pairs naturally with the planned React 19 native <title> support (same
// mental model: state-driven imperative head updates).
//
// Usage:
//   usePageMeta({
//     title: "PkPlots | About",
//     description: "About PkPlots",
//     canonical: "/about",
//     keywords: "real estate, Pakistan, …",
//   });

const upsertMeta = (name, content) => {
  if (content == null) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  if (href == null) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const usePageMeta = ({
  title,
  description,
  canonical,
  keywords,
} = {}) => {
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  useEffect(() => {
    if (description != null) upsertMeta("description", description);
  }, [description]);

  useEffect(() => {
    if (keywords != null) upsertMeta("keywords", keywords);
  }, [keywords]);

  useEffect(() => {
    if (canonical != null) upsertLink("canonical", canonical);
  }, [canonical]);
};

export default usePageMeta;
